import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { parseMrkdwn, type MrkdwnNode, type SlackAttachment, type SlackBlock, type SlackFile } from '@assembly/slack'
import { usePlatform } from '../platform'
import { useUi } from '../state/ui'
import { useConversations, useConversationName, useFileUrl, useUserGroups, useUser, displayName } from '../data/hooks'
import { useMe, useWorkspace } from '../data/workspace'
import { formatBytes, formatSlackDate } from '../format'
import { Emoji, Spinner } from './common'
import { Icon } from './Icon'

// ------------------------------------------------------------------ inline entities

function UserMention({ id, label }: { id: string; label?: string }) {
  const user = useUser(id)
  const me = useMe()
  const openPanel = useUi((s) => s.openPanel)
  return (
    <button type="button" className={`mention ${id === me ? 'mention-me' : ''}`} onClick={() => openPanel({ type: 'profile', user: id })}>
      @{user ? displayName(user) : (label ?? id)}
    </button>
  )
}

function ChannelMention({ id, label }: { id: string; label?: string }) {
  const { data: convs } = useConversations()
  const name = useConversationName()
  const openChannel = useUi((s) => s.openChannel)
  const conv = convs?.find((c) => c.id === id)
  return (
    <button type="button" className="mention" onClick={() => openChannel(id)}>
      #{conv ? name(conv) : (label ?? id)}
    </button>
  )
}

function GroupMention({ id, label }: { id: string; label?: string }) {
  const { data: groups } = useUserGroups()
  const me = useMe()
  const g = groups?.find((x) => x.id === id)
  return <span className={`mention ${g?.users?.includes(me) ? 'mention-me' : ''}`}>{g ? `@${g.handle}` : (label ?? `@${id}`)}</span>
}

export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  const platform = usePlatform()
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => {
        if (platform.kind !== 'web') {
          e.preventDefault()
          platform.openExternal(href)
        }
      }}
    >
      {children}
    </a>
  )
}

function renderNodes(nodes: MrkdwnNode[], jumbo = false): ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.type) {
      case 'text':
        return <Fragment key={i}>{n.text}</Fragment>
      case 'br':
        return <br key={i} />
      case 'bold':
        return <strong key={i}>{renderNodes(n.children)}</strong>
      case 'italic':
        return <em key={i}>{renderNodes(n.children)}</em>
      case 'strike':
        return <s key={i}>{renderNodes(n.children)}</s>
      case 'code':
        return <code key={i}>{n.text}</code>
      case 'pre':
        return <pre key={i}>{n.text}</pre>
      case 'quote':
        return <blockquote key={i}>{renderNodes(n.children)}</blockquote>
      case 'link':
        return (
          <ExternalLink key={i} href={n.url}>
            {n.label ?? n.url.replace(/^mailto:/, '')}
          </ExternalLink>
        )
      case 'user':
        return <UserMention key={i} id={n.id} label={n.label} />
      case 'channel':
        return <ChannelMention key={i} id={n.id} label={n.label} />
      case 'usergroup':
        return <GroupMention key={i} id={n.id} label={n.label} />
      case 'broadcast':
        return (
          <span key={i} className="mention mention-me">
            @{n.name}
          </span>
        )
      case 'emoji':
        return <Emoji key={i} name={n.name} skinTone={n.skinTone} size={jumbo ? 32 : undefined} />
      case 'date':
        return <Fragment key={i}>{formatSlackDate(n.ts, n.format) || n.fallback}</Fragment>
    }
  })
}

export function Mrkdwn({ text, className, inline }: { text: string; className?: string; inline?: boolean }) {
  const nodes = parseMrkdwn(text)
  const jumbo = nodes.length > 0 && nodes.length <= 23 && nodes.every((n) => n.type === 'emoji' || (n.type === 'text' && !n.text.trim()))
  const Tag = inline ? 'span' : 'div'
  return <Tag className={`mrkdwn ${inline ? 'inline' : ''} ${jumbo ? 'jumbo' : ''} ${className ?? ''}`}>{renderNodes(nodes, jumbo)}</Tag>
}

// ------------------------------------------------------------------ blocks

function Text({ t }: { t?: { type: string; text: string } }) {
  if (!t) return null
  return t.type === 'mrkdwn' ? <Mrkdwn text={t.text} /> : <div className="mrkdwn">{t.text}</div>
}

function RichTextElements({ elements }: { elements: any[] }) {
  return (
    <>
      {elements.map((el, i) => {
        let node: ReactNode
        switch (el.type) {
          case 'text':
            node = el.text
            break
          case 'link':
            node = <ExternalLink href={el.url}>{el.text ?? el.url}</ExternalLink>
            break
          case 'user':
            node = <UserMention id={el.user_id} />
            break
          case 'channel':
            node = <ChannelMention id={el.channel_id} />
            break
          case 'usergroup':
            node = <GroupMention id={el.usergroup_id} />
            break
          case 'broadcast':
            node = <span className="mention mention-me">@{el.range}</span>
            break
          case 'emoji':
            node = <Emoji name={el.name} skinTone={el.skin_tone} />
            break
          case 'date':
            node = formatSlackDate(el.timestamp, el.format ?? '{date}')
            break
          default:
            node = el.text ?? null
        }
        const s = el.style ?? {}
        if (s.code) node = <code>{node}</code>
        if (s.bold) node = <strong>{node}</strong>
        if (s.italic) node = <em>{node}</em>
        if (s.strike) node = <s>{node}</s>
        return <Fragment key={i}>{node}</Fragment>
      })}
    </>
  )
}

function RichText({ elements }: { elements: any[] }) {
  return (
    <div className="mrkdwn">
      {elements.map((el, i) => {
        switch (el.type) {
          case 'rich_text_section':
            return (
              <span key={i} className="rt-section">
                <RichTextElements elements={el.elements ?? []} />
              </span>
            )
          case 'rich_text_preformatted':
            return (
              <pre key={i}>
                <RichTextElements elements={el.elements ?? []} />
              </pre>
            )
          case 'rich_text_quote':
            return (
              <blockquote key={i}>
                <RichTextElements elements={el.elements ?? []} />
              </blockquote>
            )
          case 'rich_text_list': {
            const Tag = el.style === 'ordered' ? 'ol' : 'ul'
            return (
              <Tag key={i} style={{ marginLeft: (el.indent ?? 0) * 18 }}>
                {(el.elements ?? []).map((item: any, j: number) => (
                  <li key={j}>
                    <RichTextElements elements={item.elements ?? []} />
                  </li>
                ))}
              </Tag>
            )
          }
          default:
            return null
        }
      })}
    </div>
  )
}

export function Blocks({ blocks }: { blocks: SlackBlock[] }) {
  return (
    <div className="blocks">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'section':
            return (
              <div key={i} className="block-section">
                <div className="block-section-main">
                  <Text t={b.text} />
                  {b.fields && (
                    <div className="block-fields">
                      {b.fields.map((f, j) => (
                        <Text key={j} t={f} />
                      ))}
                    </div>
                  )}
                </div>
                {b.accessory?.type === 'image' && <img className="block-accessory" src={b.accessory.image_url} alt={b.accessory.alt_text ?? ''} />}
              </div>
            )
          case 'header':
            return (
              <h3 key={i} className="block-header">
                {b.text?.text}
              </h3>
            )
          case 'divider':
            return <hr key={i} className="block-divider" />
          case 'image':
            return (
              <figure key={i} className="block-image">
                {b.title && <figcaption>{b.title.text}</figcaption>}
                <img src={b.image_url} alt={b.alt_text ?? ''} loading="lazy" />
              </figure>
            )
          case 'context':
            return (
              <div key={i} className="block-context">
                {(b.elements ?? []).map((el: any, j: number) =>
                  el.type === 'image' ? <img key={j} src={el.image_url} alt={el.alt_text ?? ''} /> : <Text key={j} t={el} />,
                )}
              </div>
            )
          case 'rich_text':
            return <RichText key={i} elements={b.elements ?? []} />
          case 'actions':
            return (
              <div key={i} className="block-actions">
                {(b.elements ?? [])
                  .filter((el: any) => el.type === 'button')
                  .map((el: any, j: number) =>
                    el.url ? (
                      <ExternalLink key={j} href={el.url}>
                        <span className="block-button">{el.text?.text}</span>
                      </ExternalLink>
                    ) : (
                      <span key={j} className="block-button disabled" title="Interactive buttons aren't supported">
                        {el.text?.text}
                      </span>
                    ),
                  )}
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}

/** Messages whose blocks carry more than the plain text (bots, workflows). */
export function hasRichBlocks(blocks?: SlackBlock[]): boolean {
  return !!blocks?.some((b) => b.type !== 'rich_text')
}

// ------------------------------------------------------------------ attachments

export function Attachments({ attachments }: { attachments: SlackAttachment[] }) {
  return (
    <>
      {attachments.map((att, i) => {
        if (att.is_msg_unfurl) {
          return (
            <div key={i} className="attachment attachment-quote">
              <div className="attachment-author">
                {att.author_icon && <img src={att.author_icon} alt="" />}
                <strong>{att.author_name}</strong>
              </div>
              <Mrkdwn text={att.text ?? ''} />
              {att.footer && <div className="attachment-footer">{att.footer}</div>}
            </div>
          )
        }
        const color = att.color ? (att.color.startsWith('#') ? att.color : `#${att.color}`) : undefined
        return (
          <div key={i} className="attachment" style={color ? { borderLeftColor: color } : undefined}>
            {att.pretext && <Mrkdwn text={att.pretext} />}
            {(att.service_name || att.author_name) && (
              <div className="attachment-author">
                {(att.service_icon || att.author_icon) && <img src={att.service_icon ?? att.author_icon} alt="" />}
                <span>{att.service_name ?? att.author_name}</span>
              </div>
            )}
            {att.title &&
              (att.title_link ? (
                <ExternalLink href={att.title_link}>
                  <strong className="attachment-title">{att.title}</strong>
                </ExternalLink>
              ) : (
                <strong className="attachment-title">{att.title}</strong>
              ))}
            {att.text && <Mrkdwn text={att.text} />}
            {att.fields && (
              <div className="attachment-fields">
                {att.fields.map((f, j) => (
                  <div key={j} className={f.short ? 'short' : ''}>
                    <strong>{f.title}</strong>
                    <Mrkdwn text={f.value} />
                  </div>
                ))}
              </div>
            )}
            {att.image_url && <img className="attachment-image" src={att.image_url} alt="" loading="lazy" />}
            {!att.image_url && att.thumb_url && <img className="attachment-thumb" src={att.thumb_url} alt="" loading="lazy" />}
            {att.footer && <div className="attachment-footer">{att.footer}</div>}
          </div>
        )
      })}
    </>
  )
}

// ------------------------------------------------------------------ files

function ImageFile({ file }: { file: SlackFile }) {
  const thumb = file.thumb_720 ?? file.thumb_480 ?? file.thumb_360 ?? file.url_private
  const { data: src, isLoading } = useFileUrl(thumb)
  const [open, setOpen] = useState(false)
  const w = file.thumb_480_w ?? file.thumb_360_w ?? file.original_w ?? 360
  const h = file.thumb_480_h ?? file.thumb_360_h ?? file.original_h ?? 240
  const scale = Math.min(1, 360 / w, 300 / h)
  return (
    <>
      <button type="button" className="file-image" style={{ width: w * scale, aspectRatio: `${w} / ${h}` }} onClick={() => setOpen(true)} title={file.name}>
        {isLoading ? <Spinner /> : src ? <img src={src} alt={file.title ?? file.name ?? ''} /> : <Icon name="image" />}
      </button>
      {open && <ImageViewer file={file} onClose={() => setOpen(false)} />}
    </>
  )
}

function ImageViewer({ file, onClose }: { file: SlackFile; onClose: () => void }) {
  const { data: src } = useFileUrl(file.url_private)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])
  // Portaled to <body> so it sits above everything, including the window's drag strip.
  return createPortal(
    <div className="image-viewer" role="dialog" aria-modal="true" aria-label={file.title ?? file.name ?? 'Image'} onClick={onClose}>
      <header onClick={(e) => e.stopPropagation()}>
        <span>{file.title ?? file.name}</span>
        <FileDownload file={file} />
        <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
          <Icon name="x" />
        </button>
      </header>
      {src ? <img src={src} alt={file.title ?? ''} onClick={(e) => e.stopPropagation()} /> : <Spinner size={32} />}
    </div>,
    document.body,
  )
}

function FileDownload({ file }: { file: SlackFile }) {
  const platform = usePlatform()
  const { account } = useWorkspace()
  const [busy, setBusy] = useState(false)
  return (
    <button
      type="button"
      className="icon-button"
      aria-label="Download"
      title="Download"
      disabled={busy}
      onClick={async (e) => {
        e.stopPropagation()
        const src = file.url_private_download ?? file.url_private
        if (!src) return
        setBusy(true)
        try {
          const blob = await platform.fetchFile(src, account.token)
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob)
          a.download = file.name ?? 'file'
          a.click()
          setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy ? <Spinner size={16} /> : <Icon name="download" />}
    </button>
  )
}

function VideoFile({ file }: { file: SlackFile }) {
  const { data: src } = useFileUrl(file.url_private)
  return src ? <video className="file-video" src={src} controls preload="metadata" /> : <GenericFile file={file} />
}

function GenericFile({ file }: { file: SlackFile }) {
  return (
    <div className="file-card">
      <span className="file-icon">
        <Icon name="file" />
      </span>
      <div className="file-meta">
        <strong>{file.title ?? file.name}</strong>
        <span>
          {file.pretty_type ?? file.filetype?.toUpperCase()} {formatBytes(file.size)}
        </span>
      </div>
      {file.permalink && (
        <ExternalLink href={file.permalink}>
          <span className="icon-button" title="Open in Slack">
            <Icon name="external" />
          </span>
        </ExternalLink>
      )}
      <FileDownload file={file} />
    </div>
  )
}

export function Files({ files }: { files: SlackFile[] }) {
  return (
    <div className="files">
      {files.map((f) => {
        if (f.mode === 'tombstone' || f.mode === 'hidden_by_limit') {
          return (
            <div key={f.id} className="file-card muted">
              {f.mode === 'tombstone' ? 'This file was deleted.' : 'This file is hidden by your workspace plan.'}
            </div>
          )
        }
        if (f.mimetype?.startsWith('image/') && (f.thumb_360 || f.url_private)) return <ImageFile key={f.id} file={f} />
        if (f.mimetype?.startsWith('video/')) return <VideoFile key={f.id} file={f} />
        return <GenericFile key={f.id} file={f} />
      })}
    </div>
  )
}
